import { Platform } from "react-native";

import { getNativeAppVersion } from "@/lib/appVersion";
import { isBelowMinAppVersion } from "@/lib/appVersionCompare";

const COMPATIBILITY_TIMEOUT_MS = 3000;

export type AppCompatibilityResponse = {
    min_version: string;
    latest_version: string;
};

/** Public compatibility document. Network and server errors return null so the app can continue. */
export async function fetchAppCompatibility(
    serverUrl: string,
): Promise<AppCompatibilityResponse | null> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), COMPATIBILITY_TIMEOUT_MS);
    try {
        const response = await fetch(
            `${serverUrl.replace(/\/$/, "")}/version.json?ts=${Date.now()}`,
            { signal: controller.signal },
        );
        if (!response.ok) {
            return null;
        }
        const body: unknown = await response.json();
        if (!body || typeof body !== "object") {
            return null;
        }
        const record = body as {
            min_version?: unknown;
            latest_version?: unknown;
        };
        if (typeof record.min_version !== "string") {
            return null;
        }
        return {
            min_version: record.min_version,
            latest_version:
                typeof record.latest_version === "string"
                    ? record.latest_version
                    : "",
        };
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Whether the native app must show the blocking update screen.
 * Web is never blocked. A failed request is treated as compatible.
 * `latest_version` is accepted and not used.
 */
export async function isNativeUpgradeRequired(
    serverUrl: string | undefined,
): Promise<boolean> {
    if (Platform.OS === "web" || !serverUrl) {
        return false;
    }
    const compatibility = await fetchAppCompatibility(serverUrl);
    if (!compatibility) {
        return false;
    }
    return isBelowMinAppVersion(
        getNativeAppVersion(),
        compatibility.min_version,
    );
}
